/**
 * DropOverlay: covers an area (the canvas, a note, the chat composer) only
 * while files are dragged over it, and says what release will do: add them,
 * add some and why not the rest, or refuse (a read-only place). The caller
 * spreads `intake.getRootProps()` on the area, which must be
 * `position: relative`; the overlay never takes pointer events, so the drag
 * keeps reaching the area and any nested target beneath it.
 *
 * The live region is always mounted, so a screen reader hears the drag even
 * though the overlay itself appears only during one.
 */

import { Box, Text, VisuallyHidden, VStack } from '@chakra-ui/react';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { dragHeadline, type FileIntake } from './useFileIntake';

export interface DropOverlayProps {
  readonly intake: FileIntake;
  /** The release headline when every file fits; defaults to "Release to add N files to …". */
  readonly title?: string;
  /** A second line when every file fits, e.g. how to drop somewhere else instead. */
  readonly hint?: string;
  /** Smaller card for tight areas (the chat composer). */
  readonly compact?: boolean;
}

const ICONS = { accept: 'south', partial: 'rule', refuse: 'lock' } as const;
const FRAME = {
  accept: { border: 'border.accent', bg: 'accent.bg' },
  partial: { border: 'border.warning', bg: 'bg.warning' },
  refuse: { border: 'border.default', bg: 'bg.surface' },
} as const;
const ICON_COLORS = {
  accept: 'text.accent',
  partial: 'text.warning',
  refuse: 'text.secondary',
} as const;

export function DropOverlay({ intake, title, hint, compact = false }: DropOverlayProps) {
  const { drag } = intake;
  const readOnly = Boolean(intake.readOnlyReason);
  const headline =
    drag.state === 'accept' && title
      ? title
      : dragHeadline(drag, intake.destinationLabel, readOnly);
  const detail = drag.state === 'accept' ? hint : drag.reason;

  return (
    <>
      {drag.state !== 'idle' && (
        // Opaque base under the tinted frame: accent.bg is translucent in dark
        // mode, and the area's own content must not show through.
        <Box
          position="absolute"
          inset={compact ? 1 : 2}
          zIndex="overlay"
          pointerEvents="none"
          bg="bg.surface"
          borderRadius={compact ? 'xl' : '2xl'}
        >
          <Box
            data-intake-state={drag.state}
            h="full"
            display="grid"
            placeItems="center"
            p={compact ? 2 : 3}
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={FRAME[drag.state].border}
            borderRadius={compact ? 'xl' : '2xl'}
            bg={FRAME[drag.state].bg}
            textAlign="center"
          >
            <VStack
              spacing={1}
              bg="bg.elevated"
              borderRadius="xl"
              boxShadow="lg"
              px={compact ? 3 : 4}
              py={compact ? 2 : 3}
              maxW="20rem"
            >
              <MaterialIcon
                name={ICONS[drag.state]}
                boxSize={compact ? 5 : 7}
                color={ICON_COLORS[drag.state]}
              />
              <Text fontWeight="semibold" fontSize={compact ? 'sm' : 'md'} color="text.primary">
                {headline}
              </Text>
              {detail && (
                <Text fontSize={compact ? 'xs' : 'sm'} color="text.secondary">
                  {detail}
                </Text>
              )}
            </VStack>
          </Box>
        </Box>
      )}
      <VisuallyHidden role="status" aria-live="polite">
        {intake.liveText}
      </VisuallyHidden>
    </>
  );
}
